import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Building2, CheckCircle2, XCircle, AlertTriangle, Pause, Play, MessageCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AdminOrgCreateDialog from "./AdminOrgCreateDialog";

const TABS = [
  { key: "pending_validation", label: "À valider", color: "bg-amber-500 text-white" },
  { key: "active", label: "Actifs", color: "bg-emerald-500 text-white" },
  { key: "info_requested", label: "Info demandée", color: "bg-orange-500 text-white" },
  { key: "suspended", label: "Suspendus", color: "bg-slate-500 text-white" },
  { key: "rejected", label: "Refusés", color: "bg-rose-500 text-white" },
  { key: "all", label: "Tout", color: "bg-slate-200 text-slate-700" },
];

const STATUS_BADGE = {
  pending_validation: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  info_requested: "bg-orange-100 text-orange-800",
  suspended: "bg-slate-200 text-slate-600",
  rejected: "bg-rose-100 text-rose-800",
};

export default function AdminOrganizations() {
  const [tab, setTab] = useState("pending_validation");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [expanded, setExpanded] = useState("");
  const [commentByOrg, setCommentByOrg] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get(`/admin/organizations${tab && tab !== "all" ? `?status=${tab}` : ""}`),
        api.get("/admin/organizations/stats"),
      ]);
      setItems(l.data.items || []);
      setStats(s.data || {});
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (org, action) => {
    const comment = commentByOrg[org.id] || "";
    if (["reject", "request_info", "suspend"].includes(action) && !comment) {
      toast.error("Commentaire requis."); return;
    }
    setBusyId(org.id);
    try {
      await api.patch(`/admin/organizations/${org.id}`, { action, admin_comment: comment });
      toast.success("Action enregistrée.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusyId(""); }
  };

  return (
    <div data-testid="admin-orgs-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-orgs-title">
            <Building2 className="text-emerald-700" size={26}/> Organismes & Collectivités
          </h1>
          <p className="text-slate-500 text-xs mt-1">Validation, suspension, et suivi des organismes partenaires.</p>
        </div>
        <div className="flex items-center gap-2">
          <AdminOrgCreateDialog onCreated={load}/>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-orgs-reload">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Actualiser
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total || 0, color: "text-slate-900" },
          { label: "À valider", value: stats.pending || 0, color: "text-amber-600" },
          { label: "Actifs", value: stats.active || 0, color: "text-emerald-600" },
          { label: "Suspendus", value: stats.suspended || 0, color: "text-slate-500" },
          { label: "Refusés", value: stats.rejected || 0, color: "text-rose-600" },
        ].map((s, i)=>(
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`admin-orgs-stat-${i}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.label}</div>
            <div className={`font-heading text-2xl font-extrabold mt-1 ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mt-5 flex flex-wrap gap-2">
        {TABS.map((t)=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${tab === t.key ? t.color + " border-transparent shadow" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
                  data-testid={`admin-orgs-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? <div className="mt-6 text-slate-500 text-sm">Chargement...</div> :
       items.length === 0 ? <div className="mt-6 bg-white border border-dashed rounded-2xl p-10 text-center text-slate-500 text-sm">Aucun organisme dans cette catégorie.</div> :
       <div className="mt-4 space-y-3">
        {items.map((o)=>(
          <div key={o.id} className={`bg-white border rounded-2xl overflow-hidden ${o.status === "pending_validation" ? "border-amber-300 shadow-sm" : "border-slate-200"}`} data-testid={`admin-org-row-${o.id}`}>
            <div className="p-4 flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_BADGE[o.status] || "bg-slate-100"}`}>{o.status}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold uppercase">{o.type_label || o.org_type}</span>
                </div>
                <div className="mt-1 font-heading font-bold text-slate-900">{o.name}</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {o.responsible_name} · {o.email} · {o.phone || "—"} · {o.city || "—"} · {o.members_count || 0} membres
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={()=>setExpanded((v)=>v === o.id ? "" : o.id)} data-testid={`admin-org-toggle-${o.id}`}>{expanded === o.id ? "Réduire" : "Détails"}</Button>
            </div>
            {expanded === o.id && (
              <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="grid md:grid-cols-2 gap-3 text-xs">
                  <div className="bg-white rounded-xl p-3 border border-slate-200">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Informations</div>
                    <div className="mt-2 space-y-1">
                      <div>Fonction : <b>{o.responsible_role || "—"}</b></div>
                      <div>Adresse : {o.address || "—"} {o.postal_code} {o.city}</div>
                      <div>Site : {o.website ? <a href={o.website} target="_blank" rel="noreferrer" className="text-blue-600">{o.website}</a> : "—"}</div>
                      <div>Domaine membres : <b>{o.email_domain ? "@"+o.email_domain : "—"}</b> {o.auto_attach_by_domain && <span className="text-emerald-600">(auto-rattachement)</span>}</div>
                      <div>Créé : {new Date(o.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-slate-200">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Description</div>
                    <div className="mt-2 whitespace-pre-line">{o.description || "—"}</div>
                    {o.admin_comment && (
                      <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 text-amber-900">
                        <b>Note admin :</b> {o.admin_comment}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Textarea rows={2} placeholder="Commentaire (obligatoire pour refuser / demander info / suspendre)"
                            value={commentByOrg[o.id] || ""}
                            onChange={(e)=>setCommentByOrg((s)=>({ ...s, [o.id]: e.target.value }))}
                            data-testid={`admin-org-comment-${o.id}`}/>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {o.status !== "active" && (
                    <Button size="sm" disabled={busyId === o.id} onClick={()=>decide(o, o.status === "suspended" ? "reactivate" : "validate")} className="btn-shine text-white" data-testid={`admin-org-validate-${o.id}`}>
                      <CheckCircle2 size={13} className="mr-1"/> {o.status === "suspended" ? "Réactiver" : "Valider"}
                    </Button>
                  )}
                  {o.status === "active" && (
                    <Button size="sm" variant="outline" disabled={busyId === o.id} onClick={()=>decide(o, "suspend")} data-testid={`admin-org-suspend-${o.id}`}>
                      <Pause size={13} className="mr-1"/> Suspendre
                    </Button>
                  )}
                  <Button size="sm" variant="outline" disabled={busyId === o.id} onClick={()=>decide(o, "request_info")} data-testid={`admin-org-info-${o.id}`}>
                    <MessageCircle size={13} className="mr-1 text-orange-600"/> Demander info
                  </Button>
                  <Button size="sm" variant="outline" disabled={busyId === o.id} onClick={()=>decide(o, "reject")} className="text-rose-600 hover:bg-rose-50" data-testid={`admin-org-reject-${o.id}`}>
                    <XCircle size={13} className="mr-1"/> Refuser
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
       </div>}
    </div>
  );
}
